import { useState, useEffect, useMemo, useRef } from 'react';

/**
 * useFrozenSort — keeps the sorted ROW ORDER frozen so per-second live-data updates don't
 * reshuffle the table. Shared by Market Watch, Positions, and Orders (one place, no duplication).
 *
 * The sort order is recomputed ONLY when:
 *   - the sort changes (sortCol / sortDir),
 *   - sortVersion is bumped (the 60-second auto timer, or a manual Re-sort click), or
 *   - the visible SET / filter changes (membershipKey).
 *
 * Between those, incoming data refreshes each row's VALUES in place while the row keeps its
 * position — so rows hold still for the full 60s instead of jumping every second when sorted by a
 * live column (Return%, LTP, IV, S/S%, …). This is what the "1-minute re-sort" was always meant to
 * do; the previous code re-sorted on every per-second data flush because the sort memo depended on
 * the row array, which gets a new reference each second.
 *
 * @param {Array}    filteredRows  filtered rows (new array reference on every data tick)
 * @param {string}   sortCol       active sort column (falsy = unsorted → natural order, no freeze)
 * @param {string}   sortDir       'asc' | 'desc'
 * @param {number}   sortVersion   bump to force a re-sort (60s timer / manual Re-sort)
 * @param {string}   membershipKey cheap string that changes on a filter/SET change, NOT on value updates
 * @param {Function} getId         row -> stable unique id
 * @param {Object}   [colAliases]  optional { sortCol: actualField } (e.g. { resultDate: 'resultDateISO' })
 * @returns {Array} rows in the frozen order, carrying current values
 */
export function useFrozenSort(filteredRows, sortCol, sortDir, sortVersion, membershipKey, getId, colAliases = {}) {
  // Latest rows, readable inside the re-sort effect without making it a dependency (which would
  // re-sort every second — the very thing we're fixing).
  const rowsRef = useRef(filteredRows);
  rowsRef.current = filteredRows;

  // Flips only on empty<->populated transitions (e.g. the first data load), NOT on per-second value
  // updates or a row crossing a value-based filter threshold — so those never force a re-sort.
  const hasRows = filteredRows.length > 0;

  const [orderIds, setOrderIds] = useState([]);

  // Recompute the frozen order ONLY on the explicit triggers below.
  useEffect(() => {
    const src = rowsRef.current || [];
    if (!sortCol) { setOrderIds(src.map(getId)); return; }
    const field = colAliases[sortCol] || sortCol;
    const ordered = [...src].sort((a, b) => {
      let va = a[field], vb = b[field];
      if (va == null) va = '';
      if (vb == null) vb = '';
      if (typeof va === 'string') { va = va.toLowerCase(); vb = (vb + '').toLowerCase(); }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    setOrderIds(ordered.map(getId));
    // getId / colAliases are logically constant; deps are only the re-sort triggers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortCol, sortDir, sortVersion, membershipKey, hasRows]);

  // Apply the frozen order to the CURRENT data: values refresh, order stays put. Rows present now
  // but not yet in the frozen order (e.g. a just-widened filter) go to the end until the next
  // re-sort, rather than being dropped.
  return useMemo(() => {
    if (!sortCol) return filteredRows;
    const byId = new Map(filteredRows.map(r => [getId(r), r]));
    const out = [];
    const seen = new Set();
    for (const id of orderIds) {
      const r = byId.get(id);
      if (r) { out.push(r); seen.add(id); }
    }
    if (out.length !== filteredRows.length) {
      for (const r of filteredRows) {
        const id = getId(r);
        if (!seen.has(id)) out.push(r);
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRows, orderIds, sortCol]);
}
